import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../lib/firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { 
  ChartBarIcon, 
  ArrowDownTrayIcon,
  CalendarIcon,
  CurrencyEuroIcon,
  BoltIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';

interface HupxRecord {
  BaseloadPrice: number;
  DeliveryDay: string;
  Price: number;
  ProductQH?: string;
  Hour?: string;
  Region: string;
  Status: string;
  Volume: number;
}

interface HupxData {
  year: number;
  month: number;
  fetched_at: any;
  record_count: number;
  data: {
    data: HupxRecord[];
    message: string;
  };
}

interface HupxDocument {
  id: string;
  data: HupxData;
}

const HupxBrowser: React.FC = () => {
  const [hupxDocuments, setHupxDocuments] = useState<HupxDocument[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<HupxDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);

  useEffect(() => {
    loadHupxData();
  }, []);

  const loadHupxData = async () => {
    try {
      setLoading(true);
      const hupxQuery = query(
        collection(db, 'hupx_data'),
        orderBy('year', 'desc')
      );
      
      const snapshot = await getDocs(hupxQuery);
      const docs: HupxDocument[] = snapshot.docs.map(doc => ({
        id: doc.id,
        data: doc.data() as HupxData
      }));
      
      setHupxDocuments(docs);
      if (docs.length > 0) {
        setSelectedDocument(docs[0]);
      }
    } catch (error) {
      console.error('Error loading HUPX data:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedRecords = useMemo(() => {
    if (!selectedDocument) return [];
    return selectedDocument.data.data.data || [];
  }, [selectedDocument]);

  const hourlyData = useMemo(() => {
    const grouped: { [key: string]: HupxRecord[] } = {};
    
    selectedRecords.forEach(record => {
      const date = record.DeliveryDay.split('T')[0];
      const hour = record.Hour || record.ProductQH;
      const key = `${date}-${hour}`;
      
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(record);
    });

    return Object.entries(grouped).map(([key, records]) => {
      const avgPrice = records.reduce((sum, r) => sum + r.Price, 0) / records.length;
      const totalVolume = records.reduce((sum, r) => sum + r.Volume, 0);
      
      return {
        key,
        date: records[0].DeliveryDay.split('T')[0],
        hour: records[0].Hour || records[0].ProductQH,
        price: avgPrice,
        volume: totalVolume,
        baseloadPrice: records[0].BaseloadPrice
      };
    }).sort((a, b) => a.key.localeCompare(b.key));
  }, [selectedRecords]);

  const statistics = useMemo(() => {
    if (hourlyData.length === 0) return null;

    const prices = hourlyData.map(d => d.price);
    const volumes = hourlyData.map(d => d.volume);

    return {
      avgPrice: (prices.reduce((a, b) => a + b, 0) / prices.length).toFixed(2),
      minPrice: Math.min(...prices).toFixed(2),
      maxPrice: Math.max(...prices).toFixed(2),
      totalVolume: volumes.reduce((a, b) => a + b, 0).toFixed(2),
      recordCount: hourlyData.length
    };
  }, [hourlyData]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return hourlyData.slice(startIndex, endIndex);
  }, [hourlyData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(hourlyData.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDocument]);


  const exportToCSV = () => {
    if (!selectedDocument) return;

    const headers = ['Date', 'Hour', 'Price (EUR/MWh)', 'Volume (MWh)', 'Baseload Price', 'Region', 'Status'];
    const rows = hourlyData.map(d => [
      d.date,
      d.hour,
      d.price.toFixed(2),
      d.volume.toFixed(2),
      d.baseloadPrice.toFixed(2),
      'HU',
      'final'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `hupx_${selectedDocument.id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BoltIcon className="w-8 h-8 text-yellow-500" />
          HUPX Böngésző
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Másnapi Piac (DAM) Aggregált Kereskedési Adatok
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Az adatok havonta egyszer frissülnek - minden hónap 1-jén az előző hónap adatai kerülnek feltöltésre
        </p>
      </div>

      {/* Month Selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Hónap kiválasztása
        </label>
        <select
          value={selectedDocument?.id || ''}
          onChange={(e) => {
            const doc = hupxDocuments.find(d => d.id === e.target.value);
            setSelectedDocument(doc || null);
          }}
          className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {hupxDocuments.map(doc => (
            <option key={doc.id} value={doc.id}>
              {doc.data.year}. {String(doc.data.month).padStart(2, '0')}. ({doc.data.record_count} rekord)
            </option>
          ))}
        </select>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center gap-2 text-blue-700 mb-1">
              <CurrencyEuroIcon className="w-5 h-5" />
              <span className="text-sm font-medium">Átlagár</span>
            </div>
            <p className="text-2xl font-bold text-blue-900">{statistics.avgPrice}</p>
            <p className="text-xs text-blue-600">EUR/MWh</p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
            <div className="flex items-center gap-2 text-green-700 mb-1">
              <span className="text-sm font-medium">Min. Ár</span>
            </div>
            <p className="text-2xl font-bold text-green-900">{statistics.minPrice}</p>
            <p className="text-xs text-green-600">EUR/MWh</p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
            <div className="flex items-center gap-2 text-red-700 mb-1">
              <span className="text-sm font-medium">Max. Ár</span>
            </div>
            <p className="text-2xl font-bold text-red-900">{statistics.maxPrice}</p>
            <p className="text-xs text-red-600">EUR/MWh</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center gap-2 text-purple-700 mb-1">
              <BoltIcon className="w-5 h-5" />
              <span className="text-sm font-medium">Összes Mennyiség</span>
            </div>
            <p className="text-2xl font-bold text-purple-900">{statistics.totalVolume}</p>
            <p className="text-xs text-purple-600">MWh</p>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-gray-700 mb-1">
              <span className="text-sm font-medium">Rekordok</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{statistics.recordCount}</p>
            <p className="text-xs text-gray-600">óra</p>
          </div>
        </div>
      )}

      {/* Table View */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dátum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Óra
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ár (EUR/MWh)
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mennyiség (MWh)
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alapár
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.map((record, index) => (
                  <tr key={record.key} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.hour}:00
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                      {record.price.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">
                      {record.volume.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">
                      {record.baseloadPrice.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      {/* Pagination */}
      {hourlyData.length > itemsPerPage && (
        <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, hourlyData.length)} / {hourlyData.length} rekord
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Előző
            </button>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {currentPage} / {totalPages} oldal
              </span>
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Következő
            </button>
          </div>
        </div>
      )}

      {hupxDocuments.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <BoltIcon className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">Nincs elérhető HUPX adat</p>
          <p className="text-sm text-gray-600 mt-1">
            Az adatok automatikusan letöltésre kerülnek minden hónap 1-jén
          </p>
        </div>
      )}
    </div>
  );
};

export default HupxBrowser;
